import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Button, TextField, IconButton, Tooltip, Stack, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar,
} from '@mui/material';
import { Add, Refresh, EditOutlined, BlockOutlined, TagOutlined } from '@mui/icons-material';
import {
  PageHeader, StatCard, FilterBar, FilterSpacer, TableSurface, headerCellSx, bodyRowSx,
  TableEmptyState, ErrorState, StatusDot,
} from '../../components/common/pageKit';
import { hsnService, HsnCode } from '../../services/hsnService';

const blank = { hsn_code: '', description: '', gst_rate: 0, category: '' };

const HsnCodesPage: React.FC = () => {
  const [rows, setRows] = useState<HsnCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HsnCode | null>(null);
  const [form, setForm] = useState<{ hsn_code: string; description: string; gst_rate: number; category: string }>(blank);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    hsnService.getAll({ limit: 500 })
      .then((r) => { setRows(r.hsnCodes || []); setError(null); })
      .catch((e) => setError(e?.message || 'Failed to load HSN codes'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.hsn_code, r.description, r.category].some((v) => (v || '').toLowerCase().includes(q));
  }), [rows, search]);

  const openNew = () => { setEditing(null); setForm(blank); setDialogOpen(true); };
  const openEdit = (r: HsnCode) => {
    setEditing(r);
    setForm({ hsn_code: r.hsn_code, description: r.description, gst_rate: Number(r.gst_rate || 0), category: r.category || '' });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.hsn_code.trim() || !form.description.trim()) { setToast('HSN code and description are required'); return; }
    setSaving(true);
    try {
      if (editing) await hsnService.update(editing.id, form);
      else await hsnService.create(form);
      setToast(editing ? 'HSN updated' : 'HSN created');
      setDialogOpen(false); load();
    } catch (e: any) { setToast(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deactivate = async (r: HsnCode) => {
    if (!window.confirm(`Deactivate HSN ${r.hsn_code}?`)) return;
    try { await hsnService.deactivate(r.id); setToast('HSN deactivated'); load(); }
    catch (e: any) { setToast(e?.message || 'Failed'); }
  };

  const activeCount = rows.filter((r) => r.is_active === undefined || !!r.is_active).length;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="HSN Codes"
        subtitle="GST HSN / SAC master — the statutory rate catalogue used across products and invoices."
        actions={(
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={load} sx={{ mr: 1 }}>Refresh</Button>
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>New HSN Code</Button>
          </>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3, maxWidth: 560 }}>
        <StatCard title="Total Codes" value={rows.length} icon={<TagOutlined />} color="#2563eb" />
        <StatCard title="Active" value={activeCount} icon={<TagOutlined />} color="#16a34a" />
      </Box>

      <FilterBar>
        <TextField size="small" placeholder="Search code / description / category" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300 }} />
        <FilterSpacer />
        <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length}</Typography>
      </FilterBar>

      {error ? <ErrorState message={error} onRetry={load} /> : (
        <TableSurface>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['HSN / SAC', 'Description', 'GST Rate', 'Category', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
                ) : !filtered.length ? (
                  <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No HSN codes" hint="Add an HSN/SAC code to build the statutory rate catalogue." /></TableCell></TableRow>
                ) : filtered.map((r) => {
                  const active = r.is_active === undefined || !!r.is_active;
                  return (
                    <TableRow key={r.id} hover sx={bodyRowSx}>
                      <TableCell><Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.hsn_code}</Typography></TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{Number(r.gst_rate || 0)}%</Typography></TableCell>
                      <TableCell>{r.category || '—'}</TableCell>
                      <TableCell><StatusDot label={active ? 'Active' : 'Inactive'} tone={active ? 'success' : 'neutral'} /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                          {active && <Tooltip title="Deactivate"><IconButton size="small" onClick={() => deactivate(r)}><BlockOutlined fontSize="small" /></IconButton></Tooltip>}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{editing ? 'Edit HSN Code' : 'New HSN Code'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label="HSN / SAC code" value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} autoFocus inputProps={{ maxLength: 8 }} />
            <TextField size="small" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline minRows={2} />
            <TextField size="small" type="number" label="GST rate (%)" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} />
            <TextField size="small" label="Category (optional)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default HsnCodesPage;
